<script lang="ts" setup>
import { useResponsive } from "@composables"
import { TRACKING_EVENTS } from "@constant"
import { EnergyType } from "@machine/form.machine"
import { TrackingPlugin } from "@plugins/tracking.plugin"
import { useFormStore } from "@stores/form.store"
import { toTypedSchema } from "@vee-validate/yup"
import { useField, useForm } from "vee-validate"
import { inject, onActivated, onDeactivated, ref } from "vue"
import { array, object, string } from "yup"

const { snapshot, send } = useFormStore()

const { isMobile } = useResponsive()

const { $track } = inject<TrackingPlugin>("tracking")!

const options = [
    { label: "Electricité", value: "elec" },
    { label: "Gaz", value: "gaz" },
    { label: "Fioul", value: "fuel" },
    { label: "Bois", value: "bois" },
    { label: "Autre/Je ne sais pas", value: "autre" },
] as { label: string; value: EnergyType }[]

// 🧾 Schéma de validation
const { errors, handleSubmit } = useForm({
    validationSchema: toTypedSchema(
        object({
            type_energie_avant_travaux: array()
                .of(string())
                .min(1, "Sélectionnez au moins un type d'énergie")
                .required("Sélectionnez un type d'énergie")
                .default(snapshot.value.context.type_energie_avant_travaux),
            type_energie_apres_travaux: array()
                .of(string())
                .min(1, "Sélectionnez au moins un type d'énergie")
                .required("Sélectionnez un type d'énergie")
                .default(snapshot.value.context.type_energie_apres_travaux),
        })
    ),
})

function onError() {
    $track(TRACKING_EVENTS.FORM_INAVLID, {
        formId: snapshot.value.context.formId,
        step: snapshot.value.context.step + 1,
    })
}

function onSuccess(values: any) {
    // Met à jour le contexte de la machine
    send({
        type: "UPDATE_FIELD",
        field: "type_energie_avant_travaux",
        value: values.type_energie_avant_travaux,
    })
    send({
        type: "UPDATE_FIELD",
        field: "type_energie_apres_travaux",
        value: values.type_energie_apres_travaux,
    })

    send({
        type: "SUBMIT",
    })
}

const onSubmit = handleSubmit(onSuccess, onError)

const { value: type_energie_apres_travaux } = useField(
    "type_energie_apres_travaux"
)

const { value: type_energie_avant_travaux } = useField(
    "type_energie_avant_travaux"
)

const timeCompletion = ref(new Date().getTime())

onActivated(() => {
    timeCompletion.value = new Date().getTime()
    $track(TRACKING_EVENTS.STEP_ENTERED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
    })
})

onDeactivated(() => {
    $track(TRACKING_EVENTS.STEP_COMPLETED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
        timeCompletion: (
            (new Date().getTime() - timeCompletion.value) /
            1000
        ).toFixed(1),
    })
})
</script>

<template>
    <FormCard
        class="form-type-energy"
        title="L'essentiel"
        sub-title="Énergies utilisées avant et après les travaux"
        @submit="
            () => {
                onSubmit()
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'submit-form-type-energy',
                    buttonName: 'Suivant',
                })
            }
        "
        @back="
            () => {
                send({ type: 'BACK' })
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'back-form-type-energy',
                    buttonName: 'Retour',
                })
            }
        "
        :loading-submit="
            snapshot.matches({
                Step2: { FormTypeEnergy: 'updateField' },
            })
        "
    >
        <!-- Avant travaux -->
        <fieldset>
            <legend class="text-primary-800 font-bold mb-4">
                <span class="underline">Avant travaux</span>, quelles étaient
                les sources d'énergie à votre domicile ?
            </legend>
            <BaseCheckboxList
                input-id="type_energie_avant_travaux"
                :options="options"
                name="type_energie_avant_travaux"
                option-label="label"
                option-value="value"
                v-model="type_energie_avant_travaux"
                :horizontal="!isMobile"
                :error="errors.type_energie_avant_travaux"
                :invalid="!!errors.type_energie_avant_travaux"
            />
        </fieldset>
        <!-- Après travaux -->
        <fieldset>
            <legend class="text-primary-800 font-bold mb-4">
                <span class="underline">Après travaux</span>, quelles étaient
                les sources d'énergie à votre domicile ?
            </legend>
            <BaseCheckboxList
                input-id="type_energie_apres_travaux"
                :options="options"
                name="type_energie_apres_travaux"
                option-label="label"
                option-value="value"
                v-model="type_energie_apres_travaux"
                :horizontal="!isMobile"
                :error="errors.type_energie_apres_travaux"
                :invalid="!!errors.type_energie_apres_travaux"
            />
        </fieldset>
    </FormCard>
</template>

<style scoped>
.form-type-energy {
    --gap-form: 3.5rem;
    --padding-form: 3.5rem;
    &:deep(.p-card-content) {
        display: flex;
        flex-direction: column;
        gap: var(--gap-form);
        padding-top: var(--padding-form);

        @media (--small-viewport) {
            --gap-form: 2.5rem;
            --padding-form: 2.5rem;
        }
    }
}
</style>
